/** UTMs por defecto cuando la URL no define los principales (source, medium, campaign). */
export const UTM_DEFAULT_SOURCE = "directo";
export const UTM_DEFAULT_MEDIUM = "landing_mkt";

const hasParam = (query, key) => {
  const v = query.get(key);
  return v != null && String(v).trim() !== "";
};

/**
 * `true` si en la query actual faltan los tres UTMs principales
 * (`utm_source`, `utm_medium`, `utm_campaign`).
 */
export const urlSinUtmsPrincipales = (query) => {
  return (
    !hasParam(query, "utm_source") && !hasParam(query, "utm_medium") && !hasParam(query, "utm_campaign")
  );
};

const readSearchParams = () => new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");

/**
 * Devuelve `{ utm_source, utm_medium, utm_campaign, utm_term }`.
 * Sin UTMs principales en la URL → defaults en source/medium; sin campaign/term.
 * Con UTMs en la URL → valores de URL + sessionStorage donde falte en query;
 * source/medium nunca quedan vacíos (fallback a defaults).
 */
export const getUtmParams = () => {
  if (typeof window === "undefined") {
    return {
      utm_source: UTM_DEFAULT_SOURCE,
      utm_medium: UTM_DEFAULT_MEDIUM,
      utm_campaign: undefined,
      utm_term: undefined,
    };
  }

  const query = readSearchParams();

  if (urlSinUtmsPrincipales(query)) {
    return {
      utm_source: UTM_DEFAULT_SOURCE,
      utm_medium: UTM_DEFAULT_MEDIUM,
      utm_campaign: undefined,
      utm_term: undefined,
    };
  }

  const utm_source =
    (hasParam(query, "utm_source") ? query.get("utm_source") : sessionStorage.getItem("utm_source")) ||
    UTM_DEFAULT_SOURCE;
  const utm_medium =
    (hasParam(query, "utm_medium") ? query.get("utm_medium") : sessionStorage.getItem("utm_medium")) ||
    UTM_DEFAULT_MEDIUM;

  const fromQueryOrSession = (key) => {
    if (hasParam(query, key)) return query.get(key);
    const stored = sessionStorage.getItem(key);
    return stored != null && String(stored).trim() !== "" ? stored : undefined;
  };

  return {
    utm_source: utm_source || UTM_DEFAULT_SOURCE,
    utm_medium: utm_medium || UTM_DEFAULT_MEDIUM,
    utm_campaign: fromQueryOrSession("utm_campaign"),
    utm_term: fromQueryOrSession("utm_term"),
  };
};

/**
 * Sin UTMs principales en la URL → guardar defaults en session y limpiar campaign/term.
 * Con UTMs en la URL → persistir en session cada param presente en query (valores no vacíos).
 */
export const saveUtmParams = () => {
  if (typeof window === "undefined") return;

  const q = readSearchParams();

  if (urlSinUtmsPrincipales(q)) {
    sessionStorage.setItem("utm_source", UTM_DEFAULT_SOURCE);
    sessionStorage.setItem("utm_medium", UTM_DEFAULT_MEDIUM);
    sessionStorage.removeItem("utm_campaign");
    sessionStorage.removeItem("utm_term");
    return;
  }

  ["utm_source", "utm_medium", "utm_campaign", "utm_term"].forEach((key) => {
    const val = q.get(key);
    if (val != null && String(val).trim() !== "") {
      sessionStorage.setItem(key, val);
    }
  });
};

/**
 * `window.location.href` normalizado: sin principales → defaults en search;
 * con principales → no pisar lo que ya viene; completar source/medium faltantes desde session o defaults.
 */
export const getCurrentUrlWithUtmDefaults = () => {
  if (typeof window === "undefined") return "";

  const current = new URL(window.location.href);
  const q = current.searchParams;

  if (urlSinUtmsPrincipales(q)) {
    q.set("utm_source", UTM_DEFAULT_SOURCE);
    q.set("utm_medium", UTM_DEFAULT_MEDIUM);
    q.delete("utm_campaign");
    q.delete("utm_term");
    current.search = q.toString();
    return current.toString();
  }

  const source =
    (hasParam(q, "utm_source") ? q.get("utm_source") : sessionStorage.getItem("utm_source")) || UTM_DEFAULT_SOURCE;
  const medium =
    (hasParam(q, "utm_medium") ? q.get("utm_medium") : sessionStorage.getItem("utm_medium")) || UTM_DEFAULT_MEDIUM;

  if (!hasParam(q, "utm_source")) q.set("utm_source", source);
  if (!hasParam(q, "utm_medium")) q.set("utm_medium", medium);

  current.search = q.toString();
  return current.toString();
};

/** `?utm_source=...&utm_medium=...` (+ campaign/term si existen), usando `getUtmParams()`. */
export const buildUtmQueryString = () => {
  const { utm_source, utm_medium, utm_campaign, utm_term } = getUtmParams();
  const parts = [
    `utm_source=${encodeURIComponent(utm_source)}`,
    `utm_medium=${encodeURIComponent(utm_medium)}`,
  ];
  if (utm_campaign) parts.push(`utm_campaign=${encodeURIComponent(utm_campaign)}`);
  if (utm_term) parts.push(`utm_term=${encodeURIComponent(utm_term)}`);
  return `?${parts.join("&")}`;
};
